import _ from 'lodash';

export const toDeci = (
  val,
  {
    decorate = false,
    absolute = false,
    precision = 2,
    formatter = (v) => v,
    prefix = '',
    suffix = '',
    zeroToEmpty = false,
  } = {},
) => {
  if (_.isNil(val)) return '';
  if (val === 0 && zeroToEmpty) return '';

  let value = parseFloat(val);
  if (isNaN(value)) return val;

  value = formatter(value);
  const isNegative = value < 0;

  const format = (num) => num.toFixed(precision).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') + suffix;
  value = isNegative && absolute ? `(${prefix}${format(Math.abs(value))})` : `${prefix}${format(value)}`;

  if (decorate && isNegative) return { style: { color: 'red' }, value };
  return value;
};

export const toDeciTable = (val, options = {}) => {
  return toDeci(val, { ...options, decorate: true, absolute: true });
};

export const toPerc = (val, options = {}) => {
  return toDeci(val, { ...options, formatter: (v) => v * 100, suffix: '%' });
};

export const toPercTable = (val, options = {}) => {
  return toPerc(val, { ...options, decorate: true, absolute: true });
};

export const confidenceLevelToInterval = (level) => (1 - level) * 100 + '%';

export const isEmptyTightData = (tight) => tight.data.every((row) => row.every((col) => !col));

export const removeEmptyRows = (tight) => {
  const data = [];
  const index = [];
  _.each(tight.data, (row, ind) => {
    if (row.some((item) => item)) {
      data.push(row);
      index.push(tight.index[ind]);
    }
  });

  tight.data = data;
  tight.index = index;

  return tight;
};

export const formatDate = (d) => {
  const dt = new Date(d);
  return dt.toLocaleString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatDate2 = (d) => {
  const date = new Date(d);
  if (!_.isDate(date) || isNaN(date)) return d;

  const options = { month: 'short', year: 'numeric' };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const formattedDate = formatter.format(date);
  const parts = formattedDate.split(' ');
  const formattedDateWithHyphen = parts[0] + '-' + parts[1];
  return formattedDateWithHyphen;
};

export const formatDateShort = (d) => {
  const date = new Date(d);
  if (!_.isDate(date) || isNaN(date)) return d;

  return date.toLocaleDateString('en-CA', { month: '2-digit', year: 'numeric' });
};

export const formatCurrency = (num, { precision = 2 } = {}) => {
  if (!_.isNumber(num)) return '';

  return (
    '$' +
    Number(num)
      .toFixed(precision)
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
  );
};

export const tablePresets = {
  percentage: (val) => toPercTable(val),
  'MAR Ratio (RoMaD)': (val) => toDeciTable(val),
  'MAR Ratio': (val) => toDeciTable(val),
  Correlation: (val) => toDeciTable(val),
  corr: (val) => toDeciTable(val),
  'Treynor Ratio': (val) => toDeciTable(val),
  Beta: (val) => toDeciTable(val),
  beta: (val) => toDeciTable(val),
  Skew: (val) => toDeciTable(val),
  'Sharpe Ratio': (val) => toDeciTable(val),
  sharpe: (val) => toDeciTable(val),
  sortino: (val) => toDeciTable(val),
  'Sortino Ratio': (val) => toDeciTable(val),
  'Omega Ratio': (val) => toDeciTable(val),
  Kurtosis: (val) => toDeciTable(val),
  CAGR: (val) => toPercTable(val),
  cagr: (val) => toPercTable(val),
  'Cumulative Return': (val) => toPercTable(val),
  'CVaR 95%': (val) => toPercTable(val),
  cvar: (val) => toPercTable(val),
  'Standard Deviation': (val) => toPercTable(val),
  'Downside Deviation': (val) => toPercTable(val),
  'std dev': (val) => toPercTable(val),
  'Maximum Drawdown': (val) => toPercTable(val),
  'max dd': (val) => toPercTable(val),
  R2: (val) => toPercTable(val, { precision: 0 }),
  VAMI: (val) => (val ? formatCurrency(val, { precision: 0 }) : ''),
};

const decimalMetrics = [
  'decimal',
  'mar ratio',
  'mar ratio (romad)',
  'correlation',
  'corr',
  'treynor ratio',
  'beta',
  'skew',
  'sharpe ratio',
  'sharpe',
  'sortino',
  'sortino ratio',
  'omega ratio',
  'kurtosis',
];
const percentageMetrics = [
  'percentage',
  'cagr',
  'cumulative return',
  'cvar',
  'cvar 95%',
  'standard deviation',
  'downside deviation',
  'std dev',
  'maximum drawdown',
  'max dd',
  'alpha',
  "jensen's alpha",
];
const wholePercentageMetrics = ['r2'];
const wholeCurrencyMetrics = ['vami'];

const metricTypes = {};

for (const key of decimalMetrics) {
  metricTypes[key] = 'decimal';
}
for (const key of percentageMetrics) {
  metricTypes[key] = 'percentage';
}
for (const key of wholePercentageMetrics) {
  metricTypes[key] = 'whole-percentage';
}
for (const key of wholeCurrencyMetrics) {
  metricTypes[key] = 'whole-currency';
}

export const getDataType = (metric) => {
  if (!metric) return 'text';
  metric = metric.toLowerCase();

  const type = metricTypes[metric];
  if (type) return type;

  console.log('type not found', type);
  return 'text';
};

export const formatDataByType = (value, type, options = {}) => {
  if (type === 'text') return value;

  if (type === 'decimal') {
    return toDeci(value, options);
  }

  if (type === 'percentage') {
    return toPerc(value, options);
  }

  if (type === 'whole-percentage') {
    return toPerc(value, { precision: 0, ...options });
  }

  if (type === 'whole-currency') {
    return value ? formatCurrency(value, { precision: 0, ...options }) : '';
  }
};

export const formatMetric = (metric, value, options = {}) => {
  const type = getDataType(metric);
  return formatDataByType(value, type, options);
};

export const formatTableMetric = (metric, value) => {
  const type = getDataType(metric);

  if (type === 'text') return value;

  if (type === 'decimal') {
    return toDeciTable(value);
  }

  if (type === 'percentage') {
    return toPercTable(value);
  }

  if (type === 'whole-percentage') {
    return toPercTable(value, { precision: 0 });
  }

  if (type === 'whole-currency') {
    return value ? formatCurrency(value, { precision: 0 }) : '';
  }
};
